import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const hsg: ContentBlock<"procedure"> = {
  id: makeId("procedure-hsg"),
  kind: "procedure",
  tags: ["testing", "her", "pre-cycle"],
  title: "HSG (Hysterosalpingogram)",
  subtitle: "X-ray dye test to check fallopian tubes and uterine cavity",
  sources: [
    {
      title:
        "Oil-Based or Water-Based Contrast for Hysterosalpingography in Infertile Women",
      authors: "Dreyer K, van Rijswijk J, Mijatovic V, et al.",
      journal: "New England Journal of Medicine",
      year: 2017,
      url: "https://www.nejm.org/doi/full/10.1056/NEJMoa1612337",
      evidenceLevel: "rct",
    },
    {
      title: "Tubal flushing for subfertility",
      authors: "Wang R, Watson A, Johnson N, et al.",
      journal: "Cochrane Database of Systematic Reviews",
      year: 2020,
      url: "https://www.cochranelibrary.com/cdsr/doi/10.1002/14651858.CD003718.pub5/full",
      evidenceLevel: "meta-analysis",
    },
  ],
  body: {
    procedureName: "Hysterosalpingogram (HSG)",
    description:
      "An HSG is a short X-ray procedure that checks whether your fallopian tubes are open and your uterine cavity is normal. A radiologist injects contrast dye through the cervix while taking real-time images. It is one of the first tests ordered during a fertility workup.",
    howItWorks:
      "You lie on an X-ray table and a thin catheter is placed through the cervix. Contrast dye is slowly injected, filling the uterus and flowing through the fallopian tubes. If the tubes are open, dye spills into the pelvic cavity. The entire procedure takes about 10-15 minutes. Some patients experience mild to moderate cramping during the injection.",
    duration: "10-15 minutes",
    costRange: { low: 200, high: 800 },
    currency: "USD",
    anesthesia: "none",
    recoveryTime: "Same day, most return to normal activities immediately",
    risks: [
      "Mild cramping during and after the procedure",
      "Rare risk of infection (<1%)",
      "Very small radiation exposure",
      "Allergic reaction to contrast dye (rare)",
    ],
    prepInstructions: [
      "Schedule between cycle days 6-12 (after your period, before ovulation)",
      "Take 600-800mg ibuprofen 30-60 minutes before the procedure",
      "Arrange for someone to drive you if you are anxious, though most drive themselves",
      "No need to fast, eat normally beforehand",
    ],
    whatToExpect: [
      "Mild to moderate cramping during the dye injection, but it lasts only a few minutes",
      "You may feel pressure as the dye fills the uterus",
      "Light spotting or watery discharge for a day or two afterward",
      "Results are usually available immediately: your doctor can see the images in real time",
      "You can return to normal activities the same day",
    ],
    results: [
      {
        label: "When you hear",
        detail:
          "The radiologist often sees whether dye spills through the tubes during the exam, but a formal report may follow later.",
      },
      {
        label: "What you learn",
        detail:
          "The report usually describes whether each tube appears open and whether the uterine cavity looks normal.",
      },
      {
        label: "Follow-up",
        detail:
          "Your fertility doctor should review the images and explain whether findings change your treatment plan.",
      },
    ],
    forWhom: "her",
    frBody: {
      procedureName: "Hystérosalpingographie (HSG)",
      description:
        "L'HSG est un examen radiologique court qui vérifie la perméabilité des trompes de Fallope et la cavité utérine. Un radiologue injecte un produit de contraste à travers le col tout en réalisant des images en temps réel. C'est l'un des premiers examens demandés dans un bilan d'infertilité.",
      howItWorks:
        "Vous êtes allongée sur une table de radiologie et un fin cathéter est placé à travers le col de l'utérus. Le produit de contraste est injecté lentement, remplit l'utérus et passe dans les trompes. Si les trompes sont perméables, le produit s'écoule dans la cavité pelvienne. L'examen entier prend environ 10 à 15 minutes. Certaines patientes ressentent des crampes légères à modérées pendant l'injection.",
      duration: "10 à 15 minutes",
      recoveryTime:
        "Le jour même, la plupart reprennent leurs activités normales immédiatement",
      risks: [
        "Crampes légères à modérées pendant et après l'examen",
        "Risque rare d'infection (<1 %)",
        "Très faible exposition aux rayons X",
        "Réaction allergique au produit de contraste (rare)",
      ],
      prepInstructions: [
        "À programmer entre le 6e et le 12e jour du cycle (après les règles, avant l'ovulation)",
        "Prenez 600 à 800 mg d'ibuprofène 30 à 60 minutes avant l'examen",
        "Prévoyez quelqu'un pour vous raccompagner si vous êtes anxieuse, même si la plupart conduisent elles-mêmes",
        "Inutile d'être à jeun, mangez normalement avant",
      ],
      whatToExpect: [
        "Crampes légères à modérées pendant l'injection du produit, ne durant que quelques minutes",
        "Sensation de pression lorsque le produit remplit l'utérus",
        "Léger spotting ou écoulement aqueux pendant un à deux jours après",
        "Résultats généralement disponibles immédiatement : votre médecin voit les images en temps réel",
        "Vous pouvez reprendre vos activités normales le jour même",
      ],
      results: [
        {
          label: "Quand vous avez des nouvelles",
          detail:
            "Le radiologue voit souvent pendant l'examen si le produit passe dans les trompes, mais un compte rendu formel peut arriver ensuite.",
        },
        {
          label: "Ce que vous apprenez",
          detail:
            "Le compte rendu décrit généralement si chaque trompe semble perméable et si la cavité utérine paraît normale.",
        },
        {
          label: "Suivi",
          detail:
            "Votre spécialiste doit revoir les images et expliquer si les résultats modifient votre plan de traitement.",
        },
      ],
    },
  },
};

registerBlock(hsg);
